import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import logger from '@/lib/logger';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useWebSocket } from '@/contexts/WebSocketContext';

const CallContext = createContext(null);

const newId = () => `call_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const RING_TIMEOUT_MS = 35_000;
// If ICE negotiation hasn't completed within this window after both sides
// accept, surface a clear error instead of hanging on "Connecting…" forever.
// 25s is generous — typical successful negotiations finish in 1–3s.
const CONNECT_TIMEOUT_MS = 25_000;

/**
 * Global state machine for 1:1 WebRTC video calls.
 * States:
 *   idle      → no call activity
 *   ringing   → outgoing call sent, awaiting accept/reject
 *   incoming  → incoming call, awaiting our accept/reject
 *   connecting→ accepted, exchanging SDP/ICE
 *   active    → media flowing
 */
export function CallProvider({ children }) {
  const { user } = useAuth();
  const { addListener } = useWebSocket();

  const [callState, setCallState] = useState('idle');
  const [peer, setPeer] = useState(null); // {id, name, email}
  const [callId, setCallId] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [callStartedAt, setCallStartedAt] = useState(null);
  // 'p2p' | 'relay' | null — determined from getStats() once connected.
  const [transport, setTransport] = useState(null);

  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const ringTimerRef = useRef(null);
  const connectTimerRef = useRef(null);
  const transportPollRef = useRef(null);
  const iceQueueRef = useRef([]); // ICE candidates that arrive before remote desc is set
  const iceServersRef = useRef([{ urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] }]);
  const hasTurnRef = useRef(false); // True when last fetched ICE response includes a turn: URL
  const isCallerRef = useRef(false);
  const stateRef = useRef('idle');

  // Keep ref in sync so async signal handler can read fresh state without re-binding
  useEffect(() => { stateRef.current = callState; }, [callState]);

  // ── Cleanup ────────────────────────────────────────────────────────────
  // Pull a snapshot of WebRTC stats from the live PeerConnection and POST
  // them to the backend so the monthly digest can aggregate quality data.
  // Best-effort: we never block call teardown on this.
  const collectAndSubmitStats = useCallback(async (pc, currentCallId) => {
    if (!pc || !currentCallId) return;
    try {
      const stats = await pc.getStats(null);
      let inboundVideo = null;
      let activePair = null;
      stats.forEach(r => {
        if (r.type === 'inbound-rtp' && r.kind === 'video') inboundVideo = r;
        if (r.type === 'candidate-pair' && r.state === 'succeeded' && (r.nominated || r.selected)) {
          activePair = r;
        }
      });
      // Find the local candidate referenced by the active pair to know if
      // we ended up on STUN-only or TURN.
      let candidate_type = '';
      if (activePair) {
        const local = stats.get(activePair.localCandidateId);
        if (local) candidate_type = (local.candidateType || '').toLowerCase();
      }
      const body = {
        bitrate_kbps: inboundVideo?.bytesReceived ? Math.round((inboundVideo.bytesReceived * 8) / 1000 / Math.max(1, (Date.now() - (callStartedAt || Date.now())) / 1000)) : 0,
        packet_loss_pct: inboundVideo && inboundVideo.packetsReceived
          ? Math.round((10000 * (inboundVideo.packetsLost || 0) / Math.max(1, (inboundVideo.packetsLost || 0) + inboundVideo.packetsReceived))) / 100
          : 0,
        jitter_ms: inboundVideo?.jitter ? Math.round(inboundVideo.jitter * 1000) : 0,
        rtt_ms: activePair?.currentRoundTripTime ? Math.round(activePair.currentRoundTripTime * 1000) : 0,
        candidate_type,
      };
      // Only meaningful if we actually had a connection
      if (body.candidate_type || body.bitrate_kbps) {
        await api.submitCallStats(currentCallId, body).catch(() => {});
      }

      // ── Post-call quality summary toast ─────────────────────────────
      // If the call was actually connected (we have either a candidate type or
      // a measurable bitrate) and one of the quality metrics is poor, surface
      // a friendly nudge toward the Always-use-relay toggle. Skip if the user
      // already has it on.
      const callDurationSec = callStartedAt ? Math.round((Date.now() - callStartedAt) / 1000) : 0;
      let forceRelay = false;
      try { forceRelay = localStorage.getItem('call_force_relay') === '1'; } catch (e) { /* noop */ }
      if (callDurationSec >= 5 && (body.candidate_type || body.bitrate_kbps)) {
        const reasons = [];
        if (body.packet_loss_pct > 2) reasons.push(`${body.packet_loss_pct}% packet loss`);
        if (body.bitrate_kbps && body.bitrate_kbps < 200) reasons.push(`${body.bitrate_kbps} kbps bitrate`);
        if (body.rtt_ms > 400) reasons.push(`${body.rtt_ms} ms latency`);
        if (reasons.length) {
          const suffix = forceRelay
            ? ' Your network may be unstable.'
            : ' Consider enabling Always-use-relay in Settings.';
          toast.warning(`Call ended with poor quality (${reasons.join(', ')}).${suffix}`, { duration: 8000 });
        }
      }
    } catch (e) {
      logger.error('collectStats', e);
    }
  }, [callStartedAt]);

  const cleanup = useCallback(() => {
    if (ringTimerRef.current) { clearTimeout(ringTimerRef.current); ringTimerRef.current = null; }
    if (connectTimerRef.current) { clearTimeout(connectTimerRef.current); connectTimerRef.current = null; }
    if (transportPollRef.current) { clearInterval(transportPollRef.current); transportPollRef.current = null; }
    const pc = pcRef.current;
    const cid = callId;
    if (pc && cid && stateRef.current === 'active') {
      // Fire-and-forget — don't block teardown on stats submission
      collectAndSubmitStats(pc, cid);
    }
    if (pc) {
      try { pc.ontrack = null; pc.onicecandidate = null; pc.close(); }
      catch (e) { logger.error('pc close', e); }
      pcRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    remoteStreamRef.current = null;
    iceQueueRef.current = [];
    isCallerRef.current = false;
    setCallId(null);
    setPeer(null);
    setIsMuted(false);
    setIsCameraOff(false);
    setCallStartedAt(null);
    setTransport(null);
    setCallState('idle');
  }, [callId, collectAndSubmitStats]);

  // ── Build the PeerConnection ──────────────────────────────────────────
  const buildPeer = useCallback((remoteId, currentCallId) => {
    // Respect per-browser "Always use relay" preference (Settings → Call Quality).
    // When enabled, the WebRTC stack will only attempt TURN candidates, which
    // bypasses the entire class of "stuck on Connecting…" symmetric-NAT bugs.
    let forceRelay = false;
    try { forceRelay = localStorage.getItem('call_force_relay') === '1'; } catch (e) { /* noop */ }
    const pc = new RTCPeerConnection({
      iceServers: iceServersRef.current,
      iceTransportPolicy: forceRelay ? 'relay' : 'all',
    });
    if (forceRelay) logger.info('[call] iceTransportPolicy=relay (user preference)');
    pc.onicecandidate = (e) => {
      if (e.candidate) {
        api.sendCallSignal(remoteId, currentCallId, 'webrtc-ice', { candidate: e.candidate.toJSON() }).catch(() => {});
      }
    };
    pc.ontrack = (e) => {
      const [stream] = e.streams;
      remoteStreamRef.current = stream;
      // Force re-render to attach to the <video> element
      setCallStartedAt(prev => prev || Date.now());
    };
    pc.onconnectionstatechange = () => {
      if (!pcRef.current) return;
      const s = pcRef.current.connectionState;
      logger.info(`[call] connectionState=${s} ice=${pcRef.current.iceConnectionState} ice-gather=${pcRef.current.iceGatheringState}`);
      if (s === 'connected') {
        if (connectTimerRef.current) { clearTimeout(connectTimerRef.current); connectTimerRef.current = null; }
        setCallState('active');
        if (!callStartedAt) setCallStartedAt(Date.now());
        // Determine the active transport (p2p vs relay) for the UI badge.
        const sampleTransport = async () => {
          const cur = pcRef.current;
          if (!cur) return;
          try {
            const stats = await cur.getStats(null);
            let activePair = null;
            stats.forEach((r) => {
              if (r.type === 'candidate-pair' && r.state === 'succeeded' && (r.nominated || r.selected)) {
                activePair = r;
              }
            });
            if (!activePair) return;
            const local = stats.get(activePair.localCandidateId);
            const type = (local?.candidateType || '').toLowerCase();
            setTransport(type === 'relay' ? 'relay' : 'p2p');
          } catch (e) {
            // Stats API may be unavailable on very old browsers — silently skip.
          }
        };
        sampleTransport();
        // ICE can renegotiate mid-call (rare but possible). Re-sample every
        // 10s while the call is active so the badge updates if it switches.
        if (transportPollRef.current) clearInterval(transportPollRef.current);
        transportPollRef.current = setInterval(sampleTransport, 10000);
      } else if (s === 'failed' || s === 'disconnected' || s === 'closed') {
        if (stateRef.current !== 'idle') {
          // Differentiate "we never got connected" from "we lost an active call".
          const hadActiveCall = stateRef.current === 'active';
          if (!hadActiveCall && !hasTurnRef.current) {
            toast.error('Could not connect. Network requires a TURN relay — STUN-only mode is in effect.');
          } else {
            toast.error(hadActiveCall ? 'Call disconnected' : 'Could not establish a connection');
          }
          cleanup();
        }
      }
    };
    pc.oniceconnectionstatechange = () => {
      if (!pcRef.current) return;
      logger.info(`[call] iceConnectionState=${pcRef.current.iceConnectionState}`);
    };
    return pc;
  }, [callStartedAt, cleanup]);

  const getLocalMedia = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      localStreamRef.current = stream;
      return stream;
    } catch (e) {
      logger.error('getUserMedia failed:', e);
      toast.error('Camera/microphone access denied');
      throw e;
    }
  }, []);

  const drainIceQueue = useCallback(async () => {
    const pc = pcRef.current;
    if (!pc) return;
    while (iceQueueRef.current.length) {
      const c = iceQueueRef.current.shift();
      try { await pc.addIceCandidate(c); } catch (e) { logger.error('addIceCandidate', e); }
    }
  }, []);

  // Helper: refresh ICE servers from backend, set hasTurnRef so we can give
  // a meaningful error if the connection later fails.
  const refreshIceServers = useCallback(async () => {
    try {
      const { data } = await api.getIceServers();
      const servers = data?.iceServers || [];
      if (servers.length) iceServersRef.current = servers;
      hasTurnRef.current = servers.some((s) => {
        const u = s.urls || s.url || '';
        return Array.isArray(u) ? u.some((x) => x.startsWith('turn:')) : String(u).startsWith('turn:');
      });
      logger.info(`[call] ICE servers loaded: ${servers.length} (TURN=${hasTurnRef.current})`);
    } catch (e) {
      logger.error('[call] getIceServers failed, using STUN-only fallback', e);
      hasTurnRef.current = false;
    }
  }, []);

  // Arm a watchdog: if we don't reach 'active' within CONNECT_TIMEOUT_MS of
  // entering 'connecting', surface a clear error to the user. Most commonly
  // caused by missing TURN credentials on production.
  const armConnectTimeout = useCallback(() => {
    if (connectTimerRef.current) clearTimeout(connectTimerRef.current);
    connectTimerRef.current = setTimeout(() => {
      if (stateRef.current === 'connecting') {
        const msg = hasTurnRef.current
          ? 'Could not establish a connection within 25 seconds. Both peers may be on incompatible networks.'
          : 'Could not connect within 25 seconds. Your network requires a TURN relay, but only STUN is configured on this server.';
        toast.error(msg, { duration: 8000 });
        // Tell the peer we're giving up so they don't sit on "Connecting…" too.
        if (peer && callId) {
          api.sendCallSignal(peer.id, callId, 'call-end', { reason: 'connect_timeout' }).catch(() => {});
        }
        cleanup();
      }
    }, CONNECT_TIMEOUT_MS);
  }, [peer, callId, cleanup]);
  const startCall = useCallback(async (partner) => {
    if (stateRef.current !== 'idle') {
      toast.error('You are already in a call');
      return;
    }
    const newCallId = newId();
    isCallerRef.current = true;
    setPeer({ id: partner.id, name: partner.name, email: partner.email });
    setCallId(newCallId);
    setCallState('ringing');

    // Refresh ICE servers from backend (cheap). Tracks TURN availability.
    await refreshIceServers();

    try {
      await api.sendCallSignal(partner.id, newCallId, 'call-request');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start call');
      cleanup();
      return;
    }

    // Auto-cancel if no answer in 35s
    ringTimerRef.current = setTimeout(() => {
      if (stateRef.current === 'ringing') {
        toast.info(`${partner.name} did not answer`);
        api.sendCallSignal(partner.id, newCallId, 'call-cancel').catch(() => {});
        cleanup();
      }
    }, RING_TIMEOUT_MS);
  }, [cleanup]);

  // ── PUBLIC: Accept an incoming call ──────────────────────────────────
  const acceptCall = useCallback(async () => {
    if (stateRef.current !== 'incoming' || !peer || !callId) return;
    setCallState('connecting');
    armConnectTimeout();
    await refreshIceServers();

    try {
      const stream = await getLocalMedia();
      const pc = buildPeer(peer.id, callId);
      pcRef.current = pc;
      stream.getTracks().forEach(t => pc.addTrack(t, stream));
      await api.sendCallSignal(peer.id, callId, 'call-accept');
    } catch (e) {
      api.sendCallSignal(peer.id, callId, 'call-reject').catch(() => {});
      cleanup();
    }
  }, [peer, callId, getLocalMedia, buildPeer, cleanup, armConnectTimeout, refreshIceServers]);

  // ── PUBLIC: Reject incoming / cancel outgoing / hang up ──────────────
  const rejectCall = useCallback(() => {
    if (peer && callId) api.sendCallSignal(peer.id, callId, 'call-reject').catch(() => {});
    cleanup();
  }, [peer, callId, cleanup]);

  const endCall = useCallback(() => {
    if (peer && callId) api.sendCallSignal(peer.id, callId, 'call-end').catch(() => {});
    cleanup();
  }, [peer, callId, cleanup]);

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    stream.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
    setIsMuted(m => !m);
  }, []);

  const toggleCamera = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    stream.getVideoTracks().forEach(t => { t.enabled = !t.enabled; });
    setIsCameraOff(c => !c);
  }, []);

  // ── Signaling listener ───────────────────────────────────────────────
  useEffect(() => {
    if (!addListener) return;
    const unsub = addListener('call_signal', async (data) => {
      const { type, from_id, from_name, from_email, call_id, payload } = data || {};
      try {
        if (type === 'call-request') {
          // If we are already in any call, auto-reject (peer side will get a reject)
          if (stateRef.current !== 'idle') {
            api.sendCallSignal(from_id, call_id, 'call-reject', { reason: 'busy' }).catch(() => {});
            return;
          }
          isCallerRef.current = false;
          setPeer({ id: from_id, name: from_name, email: from_email });
          setCallId(call_id);
          setCallState('incoming');
          // Auto-decline if not answered in 35s
          ringTimerRef.current = setTimeout(() => {
            if (stateRef.current === 'incoming') {
              api.sendCallSignal(from_id, call_id, 'call-reject', { reason: 'no_answer' }).catch(() => {});
              toast.info(`Missed call from ${from_name}`);
              cleanup();
            }
          }, RING_TIMEOUT_MS);
          return;
        }

        // From here on we only care about events that match the current call
        if (call_id && callId && call_id !== callId) return;

        if (type === 'call-accept' && isCallerRef.current) {
          if (ringTimerRef.current) { clearTimeout(ringTimerRef.current); ringTimerRef.current = null; }
          setCallState('connecting');
          armConnectTimeout();
          // Caller now sets up media + creates SDP offer
          const stream = await getLocalMedia();
          const pc = buildPeer(from_id, call_id);
          pcRef.current = pc;
          stream.getTracks().forEach(t => pc.addTrack(t, stream));
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          api.sendCallSignal(from_id, call_id, 'webrtc-offer', { sdp: offer }).catch(() => {});
          return;
        }

        if (type === 'webrtc-offer' && !isCallerRef.current) {
          const pc = pcRef.current;
          if (!pc) return;
          await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
          await drainIceQueue();
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          api.sendCallSignal(from_id, call_id, 'webrtc-answer', { sdp: answer }).catch(() => {});
          return;
        }

        if (type === 'webrtc-answer' && isCallerRef.current) {
          const pc = pcRef.current;
          if (!pc) return;
          await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
          await drainIceQueue();
          return;
        }

        if (type === 'webrtc-ice') {
          const pc = pcRef.current;
          const cand = payload?.candidate ? new RTCIceCandidate(payload.candidate) : null;
          if (!cand) return;
          if (pc && pc.remoteDescription && pc.remoteDescription.type) {
            try { await pc.addIceCandidate(cand); } catch (e) { logger.error('addIceCandidate', e); }
          } else {
            iceQueueRef.current.push(cand);
          }
          return;
        }

        if (type === 'call-reject') {
          if (isCallerRef.current && stateRef.current === 'ringing') {
            toast.info(`${peer?.name || 'Teammate'} declined the call`);
          }
          cleanup();
          return;
        }

        if (type === 'call-cancel') {
          if (stateRef.current === 'incoming') toast.info(`${from_name} canceled the call`);
          cleanup();
          return;
        }

        if (type === 'call-end') {
          toast.info('Call ended');
          cleanup();
          return;
        }
      } catch (err) {
        logger.error('call_signal handler', err);
      }
    });
    return () => { unsub?.(); };
  }, [addListener, callId, peer, getLocalMedia, buildPeer, drainIceQueue, cleanup]);

  // Hang up if user logs out
  useEffect(() => {
    if (!user && stateRef.current !== 'idle') cleanup();
  }, [user, cleanup]);

  const value = useMemo(() => ({
    callState,
    peer,
    callId,
    isMuted,
    isCameraOff,
    callStartedAt,
    transport,
    localStream: localStreamRef.current,
    remoteStream: remoteStreamRef.current,
    startCall,
    acceptCall,
    rejectCall,
    endCall,
    toggleMute,
    toggleCamera,
    // expose refs so video tiles can attach
    localStreamRef,
    remoteStreamRef,
  }), [callState, peer, callId, isMuted, isCameraOff, callStartedAt, transport, startCall, acceptCall, rejectCall, endCall, toggleMute, toggleCamera]);

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

export function useCall() {
  return useContext(CallContext);
}
